import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
const MOBILE_QUERY = "(max-width: 767px)"
const MONTH_ARROWS = [
  { selector: ".flatpickr-prev-month", label: "Previous month", delta: -1 },
  { selector: ".flatpickr-next-month", label: "Next month", delta: 1 },
]
const WHEEL_ITEM_PITCH = 36
const MIN_YEAR = 2000
const MAX_YEAR = 2100
const DEFAULT_MIN_DATE = "01/01/2000"
const DEFAULT_MAX_DATE = "12/31/2100"
const DAYS_IN_WEEK = 7

export default class extends Controller {
  static targets = ["input", "mobileActions"]
  static values = {
    mode: { type: String, default: "single" },
    dateFormat: { type: String, default: "m/d/Y" },
    minDate: String,
    maxDate: String,
    defaultDate: String,
    submitUrl: String,
    submitMethod: { type: String, default: "POST" },
    invalidMessage: { type: String, default: "Enter a valid date." },
    rangeStart: String,
    rangeEnd: String,
  }

  connect() {
    this.handleInputBeforeInput = this.handleInputBeforeInput.bind(this)
    this.handleInputBlur = this.handleInputBlur.bind(this)
    this.handleInput = this.handleInput.bind(this)
    this.handleInputKeydown = this.handleInputKeydown.bind(this)
    this.handleInputPaste = this.handleInputPaste.bind(this)
    this.handleMobileCancel = this.handleMobileCancel.bind(this)
    this.handleMobileConfirm = this.handleMobileConfirm.bind(this)
    this.handleMobileBackdrop = this.handleMobileBackdrop.bind(this)
    this.handleMobileKeydown = this.handleMobileKeydown.bind(this)
    this.handleEscapeKeydown = this.handleEscapeKeydown.bind(this)
    this.handleRangePairChange = this.handleRangePairChange.bind(this)
    this.mobileScrollTimers = new Map()
    this.picker = flatpickr(this.inputTarget, this.options)
    this.lastDispatchedValue = this.inputTarget.value
    this.calendarTrigger = this.element.querySelector("button.date-input-aq__icon")
    if (this.calendarTrigger) {
      this.handleCalendarTriggerMouseDown = (event) => {
        event.preventDefault()
        this.ignoreNextBlur = true
      }
      this.calendarTrigger.addEventListener("mousedown", this.handleCalendarTriggerMouseDown)
    }
    this.inputTarget.addEventListener("beforeinput", this.handleInputBeforeInput)
    this.inputTarget.addEventListener("blur", this.handleInputBlur, true)
    this.inputTarget.addEventListener("input", this.handleInput)
    this.inputTarget.addEventListener("keydown", this.handleInputKeydown)
    this.inputTarget.addEventListener("paste", this.handleInputPaste)
    this.element.addEventListener("keydown", this.handleEscapeKeydown)
    this.setupRangePair()
  }

  disconnect() {
    this.closeMonthYearPanel()
    this.closeMobilePicker()
    this.inputTarget.removeEventListener("beforeinput", this.handleInputBeforeInput)
    this.inputTarget.removeEventListener("blur", this.handleInputBlur, true)
    this.inputTarget.removeEventListener("input", this.handleInput)
    this.inputTarget.removeEventListener("keydown", this.handleInputKeydown)
    this.inputTarget.removeEventListener("paste", this.handleInputPaste)
    this.element.removeEventListener("keydown", this.handleEscapeKeydown)
    this.rangePairInput?.removeEventListener("input", this.handleRangePairChange)
    this.rangePairInput?.removeEventListener("change", this.handleRangePairChange)
    this.calendarTrigger?.removeEventListener("mousedown", this.handleCalendarTriggerMouseDown)
    this.mobilePicker?.remove()
    this.picker?.destroy()
  }

  handleInputBlur(event) {
    if (this.shouldSkipBlurCommit(event)) return

    this.commitTypedValue({ clearInvalid: true })
  }

  shouldSkipBlurCommit(event) {
    if (this.ignoreNextBlur) {
      this.ignoreNextBlur = false
      return true
    }

    const related = event?.relatedTarget
    if (related instanceof Element && related === this.calendarTrigger) return true

    return false
  }

  keepFocus(event) {
    event.preventDefault()
    this.ignoreNextBlur = true
  }

  open() {
    if (this.inputTarget.disabled) return

    if (this.shouldUseMobilePicker) {
      this.openMobilePicker()
      return
    }

    this.picker?.open()
  }

  handleInputBeforeInput(event) {
    if (!this.shouldFormatSingleInput || !event.data) return
    if (/^\d+$/.test(event.data)) return

    event.preventDefault()
  }

  handleInput() {
    if (!this.shouldFormatSingleInput) return

    const formattedValue = this.formatDigits(this.inputTarget.value)
    if (this.inputTarget.value !== formattedValue) this.inputTarget.value = formattedValue
  }

  handleInputKeydown(event) {
    if (event.key !== "Enter") return

    event.preventDefault()
    this.commitTypedValue({ clearInvalid: true })
  }

  handleEscapeKeydown(event) {
    if (event.key !== "Escape") return

    this.closeMonthYearPanel()
    this.picker?.close()
    this.inputTarget.focus()
  }

  handleInputPaste(event) {
    if (!this.shouldFormatSingleInput) return

    event.preventDefault()
    this.inputTarget.value = this.formatDigits(event.clipboardData.getData("text"))
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  handlePickerError(error) {
    const invalidValue = error.message?.match(/^Invalid date provided:\s*(.*)$/)?.[1]
    if (invalidValue && invalidValue === this.inputTarget.value.trim()) return

    console.warn(error)
  }

  get options() {
    const rangeMode = this.modeValue === "range"

    return {
      allowInput: true,
      appendTo: this.element,
      clickOpens: false,
      dateFormat: this.dateFormatValue,
      defaultDate: this.defaultDateValue || undefined,
      disableMobile: true,
      errorHandler: (error) => this.handlePickerError(error),
      maxDate: this.maxDateValue || DEFAULT_MAX_DATE,
      minDate: this.minDateValue || DEFAULT_MIN_DATE,
      mode: rangeMode ? "range" : "single",
      monthSelectorType: "static",
      nextArrow: this.arrowSvg("next"),
      parseDate: (dateString) => this.parseTypedDate(dateString),
      position: (instance) => this.positionCalendar(instance),
      positionElement: this.inputTarget,
      prevArrow: this.arrowSvg("previous"),
      locale: { firstDayOfWeek: 1 },
      onReady: (_selectedDates, _dateStr, instance) => {
        this.decorateCalendar(instance)
        this.installMonthYearPanel(instance)
      },
      onOpen: (_selectedDates, _dateStr, instance) => {
        if (this.shouldUseMobilePicker) {
          instance.close()
          this.openMobilePicker()
          return
        }

        this.decorateCalendar(instance)
        this.updateMonthYearPanel(instance)
      },
      onMonthChange: (_selectedDates, _dateStr, instance) => {
        this.updateMonthYearPanel(instance)
        this.updateDayTabStop(instance)
      },
      onYearChange: (_selectedDates, _dateStr, instance) => {
        this.updateMonthYearPanel(instance)
        this.updateDayTabStop(instance)
      },
      onDayCreate: (_selectedDates, _dateStr, instance, day) => {
        this.decorateDay(day)
        this.highlightRangeStartDay(day)
        this.updateDayTabStop(instance)
      },
      onChange: (_selectedDates, _dateStr, instance) => {
        this.closeMonthYearPanel()
        this.updateDayTabStop(instance)
        this.clearInputValidity()
        this.dispatchChange()
        this.syncRangePair()
        this.submitValue(instance.input.value)
      },
    }
  }

  get shouldUseMobilePicker() {
    return this.modeValue !== "range" && window.matchMedia(MOBILE_QUERY).matches
  }

  get shouldFormatSingleInput() {
    return this.modeValue !== "range" && !this.inputTarget.disabled && !this.inputTarget.readOnly
  }

  decorateCalendar(instance) {
    instance.calendarContainer.classList.add("date-picker-aq-calendar")
    instance.calendarContainer.dataset.mode = this.modeValue
    instance.calendarContainer.setAttribute("role", "dialog")
    instance.calendarContainer.setAttribute("aria-label", "Choose date")
    const daysGrid = instance.calendarContainer.querySelector(".flatpickr-days")
    daysGrid?.setAttribute("role", "grid")
    daysGrid?.setAttribute("aria-label", "Choose date")
    daysGrid?.setAttribute("aria-colcount", DAYS_IN_WEEK.toString())
    instance.calendarContainer.querySelectorAll(".dayContainer").forEach((container) => {
      container.removeAttribute("role")
      container.removeAttribute("aria-label")
      container.removeAttribute("aria-colcount")
    })
    instance.calendarContainer.querySelectorAll(".flatpickr-weekdaycontainer").forEach((container) => {
      container.setAttribute("role", "row")
    })
    instance.calendarContainer.querySelectorAll(".flatpickr-weekday").forEach((weekday) => {
      weekday.setAttribute("role", "columnheader")
    })
    this.exposeMonthArrows(instance)
    this.updateDayTabStop(instance)
    this.updateCalendarLiveRegion(instance)
  }

  exposeMonthArrows(instance) {
    MONTH_ARROWS.forEach(({ selector, label, delta }) => {
      let arrow = instance.calendarContainer.querySelector(selector)
      if (!arrow) return

      if (arrow.tagName === "BUTTON") {
        arrow.type = "button"
        arrow.setAttribute("aria-label", label)
        arrow.dataset.keyboardReady = "true"
        return
      }

      if (arrow.dataset.keyboardReady === "true") return

      const button = document.createElement("button")
      button.type = "button"
      button.className = arrow.className
      button.innerHTML = arrow.innerHTML
      button.setAttribute("aria-label", label)
      button.dataset.keyboardReady = "true"
      button.addEventListener("click", (event) => {
        event.preventDefault()
        instance.changeMonth(delta)
      })
      button.addEventListener("keydown", (event) => {
        if (!["Enter", " "].includes(event.key)) return

        event.preventDefault()
        button.click()
      })
      arrow.replaceWith(button)
    })
  }

  updateDayTabStop(instance) {
    const days = Array.from(instance.calendarContainer.querySelectorAll(".flatpickr-day"))
    if (days.length === 0) return

    const enabled = days.filter((day) => !day.classList.contains("flatpickr-disabled"))
    const stop = enabled.find((day) => day.classList.contains("selected")) ||
      enabled.find((day) => day.classList.contains("today")) ||
      enabled[0]

    days.forEach((day) => day.setAttribute("tabindex", day === stop ? "0" : "-1"))
  }

  positionCalendar(instance) {
    const calendar = instance.calendarContainer
    if (!calendar) return

    const wrapperBounds = this.element.getBoundingClientRect()
    const inputBounds = this.inputTarget.getBoundingClientRect()
    const calendarWidth = calendar.offsetWidth || calendar.getBoundingClientRect().width || 284
    const calendarHeight = calendar.offsetHeight || calendar.getBoundingClientRect().height || 320
    const clipBounds = this.calendarClipBounds()
    const padding = 8
    const gap = 2

    let calendarLeft = inputBounds.left

    if (calendarLeft + calendarWidth > clipBounds.right - padding) {
      calendarLeft = inputBounds.right - calendarWidth
    }

    if (calendarLeft < clipBounds.left + padding) {
      calendarLeft = clipBounds.left + padding
    }

    if (calendarLeft + calendarWidth > clipBounds.right - padding) {
      calendarLeft = clipBounds.right - padding - calendarWidth
    }

    const spaceBelow = clipBounds.bottom - inputBounds.bottom - padding
    const spaceAbove = inputBounds.top - clipBounds.top - padding
    const openAbove = spaceBelow < calendarHeight && spaceAbove > spaceBelow

    const calendarTop = openAbove
      ? inputBounds.top - wrapperBounds.top - calendarHeight - gap
      : inputBounds.bottom - wrapperBounds.top + gap

    calendar.style.top = `${calendarTop}px`
    calendar.style.left = `${calendarLeft - wrapperBounds.left}px`
    calendar.style.right = "auto"
    calendar.classList.toggle("date-picker-aq-calendar--above", openAbove)
  }

  calendarClipBounds() {
    const padding = 8
    let clipBounds = {
      left: padding,
      right: window.innerWidth - padding,
      top: padding,
      bottom: window.innerHeight - padding,
    }

    let node = this.element.parentElement
    while (node && node !== document.body) {
      const { overflow, overflowX, overflowY } = window.getComputedStyle(node)
      const clipsX =
        overflowX === "hidden" ||
        overflowX === "clip" ||
        overflow === "hidden" ||
        overflow === "clip"
      const clipsY =
        overflowY === "hidden" ||
        overflowY === "clip" ||
        overflow === "hidden" ||
        overflow === "clip"

      if (clipsX || clipsY) {
        const bounds = node.getBoundingClientRect()
        if (clipsX) {
          clipBounds = {
            ...clipBounds,
            left: Math.max(clipBounds.left, bounds.left),
            right: Math.min(clipBounds.right, bounds.right),
          }
        }
        if (clipsY) {
          clipBounds = {
            ...clipBounds,
            top: Math.max(clipBounds.top, bounds.top),
            bottom: Math.min(clipBounds.bottom, bounds.bottom),
          }
        }
      }
      node = node.parentElement
    }

    return clipBounds
  }

  decorateDay(day) {
    const dayIndex = Number.isInteger(day.$i) ? day.$i : Array.from(day.parentElement?.children || []).indexOf(day)

    day.setAttribute("role", "gridcell")

    if (dayIndex >= 0) {
      day.setAttribute("aria-colindex", ((dayIndex % DAYS_IN_WEEK) + 1).toString())
      day.setAttribute("aria-rowindex", (Math.floor(dayIndex / DAYS_IN_WEEK) + 1).toString())
    }

    day.setAttribute("tabindex", "-1")

    if (day.classList.contains("flatpickr-disabled")) {
      day.setAttribute("aria-disabled", "true")
    } else {
      day.removeAttribute("aria-disabled")
    }

    const selected = day.classList.contains("selected") || day.classList.contains("startRange") || day.classList.contains("endRange")
    day.setAttribute("aria-selected", selected.toString())
  }

  updateCalendarLiveRegion(instance) {
    const header = instance.calendarContainer.querySelector(".flatpickr-current-month")
    if (!header) return

    header.setAttribute("aria-live", "polite")
    header.setAttribute("aria-atomic", "true")
  }

  setupRangePair() {
    if (!this.hasRangeStartValue || !this.hasRangeEndValue) return

    this.rangeStartInput = document.querySelector(this.rangeStartValue)
    this.rangeEndInput = document.querySelector(this.rangeEndValue)
    this.rangeRole = this.inputTarget === this.rangeStartInput ? "start" : this.inputTarget === this.rangeEndInput ? "end" : null
    if (!this.rangeRole) return

    this.rangePairInput = this.rangeRole === "start" ? this.rangeEndInput : this.rangeStartInput

    this.rangePairInput?.addEventListener("input", this.handleRangePairChange)
    this.rangePairInput?.addEventListener("change", this.handleRangePairChange)
    this.syncRangePair()
  }

  handleRangePairChange() {
    this.syncRangePair()
    this.picker?.redraw()
  }

  syncRangePair() {
    if (!this.picker) return

    const startDate = this.rangeDate(this.rangeStartInput)
    const endDate = this.rangeDate(this.rangeEndInput)

    if (this.rangeRole === "start") {
      this.picker.set("maxDate", endDate ? this.addDays(endDate, -1) : this.maxDateValue || DEFAULT_MAX_DATE)
    } else if (this.rangeRole === "end") {
      this.picker.set("minDate", startDate ? this.addDays(startDate, 1) : this.minDateValue || DEFAULT_MIN_DATE)
    }
  }

  highlightRangeStartDay(day) {
    if (this.rangeRole !== "end") return

    const startDate = this.rangeDate(this.rangeStartInput)
    if (startDate && day.dateObj && this.dateOnlyTime(day.dateObj) === this.dateOnlyTime(startDate)) {
      day.classList.add("date-picker-aq-calendar__range-start")
    }
  }

  rangeDate(input) {
    return input?.value ? this.parseTypedDate(input.value) : null
  }

  installMonthYearPanel(instance) {
    const calendar = instance.calendarContainer
    const header = calendar.querySelector(".flatpickr-current-month")

    if (!header || calendar.dataset.monthYearPanelInstalled === "true") return

    calendar.dataset.monthYearPanelInstalled = "true"
    this.monthPanelYear = instance.currentYear
    this.monthPanelId = `${this.inputTarget.id || this.inputTarget.name || "date-picker"}-month-panel`.replace(/[^A-Za-z0-9_-]/g, "-")

    header.classList.add("date-picker-aq-calendar__month-trigger")
    header.setAttribute("role", "button")
    header.setAttribute("tabindex", "0")
    header.setAttribute("aria-haspopup", "dialog")
    header.setAttribute("aria-controls", this.monthPanelId)
    header.setAttribute("aria-expanded", "false")
    header.setAttribute("aria-label", "Choose month and year")

    const yearInput = header.querySelector(".cur-year")
    yearInput?.setAttribute("readonly", "readonly")

    header.addEventListener("click", (event) => {
      event.preventDefault()
      this.toggleMonthYearPanel(instance)
    })

    header.addEventListener("keydown", (event) => {
      if (!["Enter", " "].includes(event.key)) return

      event.preventDefault()
      this.toggleMonthYearPanel(instance)
    })

    calendar.appendChild(this.buildMonthYearPanel(instance))
  }

  buildMonthYearPanel(instance) {
    const panel = document.createElement("div")
    panel.id = this.monthPanelId
    panel.className = "date-picker-aq-calendar__month-panel"
    panel.hidden = true
    panel.setAttribute("role", "dialog")
    panel.setAttribute("aria-label", "Choose month")

    const header = document.createElement("div")
    header.className = "date-picker-aq-calendar__month-panel-header"

    const previous = this.monthYearButton("previous", "Previous year")
    const title = document.createElement("span")
    title.className = "date-picker-aq-calendar__month-panel-title"
    title.setAttribute("aria-live", "polite")
    title.setAttribute("aria-atomic", "true")
    const next = this.monthYearButton("next", "Next year")

    previous.addEventListener("click", () => this.changePanelYear(instance, -1))
    next.addEventListener("click", () => this.changePanelYear(instance, 1))

    header.append(previous, title, next)

    const grid = document.createElement("div")
    grid.className = "date-picker-aq-calendar__month-grid"

    MONTHS.forEach((month, index) => {
      const button = document.createElement("button")
      button.className = "date-picker-aq-calendar__month-option"
      button.type = "button"
      button.dataset.month = index
      button.textContent = month
      button.addEventListener("click", () => this.selectPanelMonth(instance, index))
      grid.appendChild(button)
    })

    panel.append(header, grid)
    return panel
  }

  monthYearButton(direction, label) {
    const button = document.createElement("button")
    button.className = `date-picker-aq-calendar__month-panel-nav date-picker-aq-calendar__month-panel-nav--${direction}`
    button.type = "button"
    button.setAttribute("aria-label", label)
    button.innerHTML = this.arrowSvg(direction === "next" ? "next" : "previous")
    return button
  }

  toggleMonthYearPanel(instance) {
    const isOpen = instance.calendarContainer.classList.contains("date-picker-aq-calendar--choosing-month")

    if (isOpen) {
      this.closeMonthYearPanel()
    } else {
      this.openMonthYearPanel(instance)
    }
  }

  openMonthYearPanel(instance) {
    this.monthPanelYear = instance.currentYear
    this.setMonthYearPanelVisibility(instance, true)
    this.updateMonthYearPanel(instance)
    this.focusCurrentMonthOption(instance)
  }

  closeMonthYearPanel({ restoreFocus = false } = {}) {
    if (!this.picker) return

    this.setMonthYearPanelVisibility(this.picker, false)
    if (restoreFocus) this.picker.calendarContainer.querySelector(".flatpickr-current-month")?.focus()
  }

  focusCurrentMonthOption(instance) {
    const panel = instance.calendarContainer.querySelector(".date-picker-aq-calendar__month-panel")
    const option = panel?.querySelector(".date-picker-aq-calendar__month-option.is-selected") ||
      panel?.querySelector(".date-picker-aq-calendar__month-option")

    option?.focus()
  }

  changePanelYear(instance, step) {
    this.monthPanelYear = (this.monthPanelYear || instance.currentYear) + step
    this.updateMonthYearPanel(instance)
  }

  selectPanelMonth(instance, monthIndex) {
    const year = this.monthPanelYear || instance.currentYear

    instance.jumpToDate(new Date(year, monthIndex, 1), false)
    instance.currentYear = year
    instance.currentMonth = monthIndex
    instance.redraw()
    this.closeMonthYearPanel({ restoreFocus: true })
    this.updateDayTabStop(instance)
  }

  setMonthYearPanelVisibility(instance, visible) {
    const calendar = instance.calendarContainer
    const panel = calendar.querySelector(".date-picker-aq-calendar__month-panel")

    calendar.classList.toggle("date-picker-aq-calendar--choosing-month", visible)
    calendar.querySelector(".flatpickr-current-month")?.setAttribute("aria-expanded", visible.toString())
    calendar.querySelector(".flatpickr-months")?.toggleAttribute("hidden", visible)
    calendar.querySelector(".flatpickr-innerContainer")?.toggleAttribute("hidden", visible)

    if (panel) panel.hidden = !visible
  }

  updateMonthYearPanel(instance) {
    const calendar = instance.calendarContainer
    const panel = calendar.querySelector(".date-picker-aq-calendar__month-panel")
    this.updateCalendarLiveRegion(instance)
    if (!panel) return

    const year = this.monthPanelYear || instance.currentYear
    const title = panel.querySelector(".date-picker-aq-calendar__month-panel-title")

    title.textContent = year

    panel.querySelectorAll(".date-picker-aq-calendar__month-option").forEach((button) => {
      const month = Number(button.dataset.month)
      const selected = month === instance.currentMonth && year === instance.currentYear

      button.classList.toggle("is-selected", selected)
      button.setAttribute("aria-pressed", selected.toString())
    })
  }

  openMobilePicker() {
    this.closeMonthYearPanel()
    this.picker?.close()
    this.ensureMobilePicker()

    this.mobileDate = this.selectedDateForMobile()
    this.renderMobileWheels()
    this.mobilePicker.hidden = false
    this.mobilePicker.classList.add("is-open")
    this.mobilePicker.querySelector(".date-picker-aq-mobile__cancel")?.focus()
    document.addEventListener("keydown", this.handleMobileKeydown)

    requestAnimationFrame(() => this.scrollMobileWheelsToDate(false))
  }

  closeMobilePicker() {
    if (!this.mobilePicker) return

    this.mobilePicker.hidden = true
    this.mobilePicker.classList.remove("is-open")
    document.removeEventListener("keydown", this.handleMobileKeydown)
    this.mobileScrollTimers?.forEach((timer) => window.clearTimeout(timer))
    this.mobileScrollTimers?.clear()
  }

  handleMobileCancel() {
    this.closeMobilePicker()
  }

  handleMobileConfirm() {
    if (!this.mobileDate) return

    this.picker.setDate(this.mobileDate, true)
    this.clearInputValidity()
    this.closeMobilePicker()
  }

  handleMobileBackdrop(event) {
    if (event.target === this.mobilePicker) this.handleMobileCancel()
  }

  handleMobileKeydown(event) {
    if (event.key === "Escape") this.handleMobileCancel()
  }

  ensureMobilePicker() {
    if (this.mobilePicker) return

    const picker = document.createElement("div")
    picker.className = "date-picker-aq-mobile"
    picker.hidden = true
    picker.setAttribute("role", "dialog")
    picker.setAttribute("aria-modal", "true")
    picker.setAttribute("aria-label", "Choose date")

    picker.innerHTML = `
      <div class="date-picker-aq-mobile__sheet">
        ${this.mobileActionsMarkup}
        <div class="date-picker-aq-mobile__wheel" aria-live="polite">
          <span class="date-picker-aq-mobile__selection" aria-hidden="true"></span>
          <div class="date-picker-aq-mobile__column date-picker-aq-mobile__column--month" data-wheel="month" role="listbox" aria-label="Month" tabindex="0"></div>
          <div class="date-picker-aq-mobile__column date-picker-aq-mobile__column--day" data-wheel="day" role="listbox" aria-label="Day" tabindex="0"></div>
          <div class="date-picker-aq-mobile__column date-picker-aq-mobile__column--year" data-wheel="year" role="listbox" aria-label="Year" tabindex="0"></div>
        </div>
      </div>
    `

    picker.addEventListener("click", this.handleMobileBackdrop)
    picker.querySelector(".date-picker-aq-mobile__cancel").addEventListener("click", this.handleMobileCancel)
    picker.querySelector(".date-picker-aq-mobile__confirm").addEventListener("click", this.handleMobileConfirm)
    picker.querySelectorAll(".date-picker-aq-mobile__column").forEach((column) => {
      column.addEventListener("scroll", () => this.handleMobileWheelScroll(column))
      column.addEventListener("keydown", (event) => this.handleMobileWheelKeydown(event, column))
    })

    document.body.appendChild(picker)
    this.mobilePicker = picker
  }

  get mobileActionsMarkup() {
    if (this.hasMobileActionsTarget) return this.mobileActionsTarget.innerHTML

    return `
      <div class="date-picker-aq-mobile__actions">
        <button class="btn btn-secondary btn-sm date-picker-aq-mobile__cancel" type="button">Cancel</button>
        <button class="btn btn-primary btn-sm date-picker-aq-mobile__confirm" type="button">Confirm</button>
      </div>
    `
  }

  selectedDateForMobile() {
    const selectedDate = this.picker?.selectedDates?.[0]
    if (selectedDate) return new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate())

    const parsedDate = this.inputTarget.value ? this.parseTypedDate(this.inputTarget.value) : null
    if (parsedDate) return new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate())

    const fallback = new Date()
    return new Date(fallback.getFullYear(), fallback.getMonth(), fallback.getDate())
  }

  renderMobileWheels() {
    this.renderMobileWheel("month", MONTH_NAMES.map((label, value) => ({ label, value })), this.mobileDate.getMonth())
    this.renderMobileWheel("day", this.mobileDayOptions(), this.mobileDate.getDate())
    this.renderMobileWheel("year", this.mobileYearOptions(), this.mobileDate.getFullYear())
  }

  renderMobileWheel(name, options, selectedValue) {
    const column = this.mobilePicker.querySelector(`[data-wheel="${name}"]`)
    const currentValues = Array.from(column.children).map((child) => child.dataset.value).join(",")
    const nextValues = options.map((option) => option.value.toString()).join(",")

    if (currentValues !== nextValues) {
      column.innerHTML = options.map((option) => (
        `<button class="date-picker-aq-mobile__option" type="button" role="option" data-value="${option.value}">${option.label}</button>`
      )).join("")

      column.querySelectorAll(".date-picker-aq-mobile__option").forEach((button) => {
        button.addEventListener("click", () => this.selectMobileWheelValue(name, Number(button.dataset.value), true))
      })
    }

    column.querySelectorAll(".date-picker-aq-mobile__option").forEach((button) => {
      const selected = Number(button.dataset.value) === selectedValue
      button.classList.toggle("is-selected", selected)
      button.setAttribute("aria-selected", selected.toString())
    })
  }

  mobileDayOptions() {
    const days = new Date(this.mobileDate.getFullYear(), this.mobileDate.getMonth() + 1, 0).getDate()
    return Array.from({ length: days }, (_value, index) => {
      const day = index + 1
      return { label: day.toString(), value: day }
    })
  }

  mobileYearOptions() {
    const selectedYear = this.mobileDate.getFullYear()
    const minimumYear = this.picker.config.minDate?.getFullYear?.() || selectedYear - 100
    const maximumYear = this.picker.config.maxDate?.getFullYear?.() || selectedYear + 100

    return Array.from({ length: maximumYear - minimumYear + 1 }, (_value, index) => {
      const year = minimumYear + index
      return { label: year.toString(), value: year }
    })
  }

  scrollMobileWheelsToDate(smooth = true) {
    this.scrollMobileWheelToValue("month", this.mobileDate.getMonth(), smooth)
    this.scrollMobileWheelToValue("day", this.mobileDate.getDate(), smooth)
    this.scrollMobileWheelToValue("year", this.mobileDate.getFullYear(), smooth)
  }

  scrollMobileWheelToValue(name, value, smooth = true) {
    const column = this.mobilePicker.querySelector(`[data-wheel="${name}"]`)
    const options = Array.from(column.querySelectorAll(".date-picker-aq-mobile__option"))
    const index = options.findIndex((option) => Number(option.dataset.value) === value)

    if (index < 0) return

    column.scrollTo({
      top: index * WHEEL_ITEM_PITCH,
      behavior: smooth ? "smooth" : "auto",
    })
  }

  handleMobileWheelScroll(column) {
    const timer = this.mobileScrollTimers.get(column)
    if (timer) window.clearTimeout(timer)

    this.mobileScrollTimers.set(column, window.setTimeout(() => {
      const wheel = column.dataset.wheel
      const options = Array.from(column.querySelectorAll(".date-picker-aq-mobile__option"))
      const index = Math.max(0, Math.min(options.length - 1, Math.round(column.scrollTop / WHEEL_ITEM_PITCH)))
      const option = options[index]

      if (!option) return

      this.selectMobileWheelValue(wheel, Number(option.dataset.value), false)
      this.scrollMobileWheelsToDate()
    }, 80))
  }

  handleMobileWheelKeydown(event, column) {
    if (!["ArrowUp", "ArrowDown"].includes(event.key)) return

    event.preventDefault()

    const wheel = column.dataset.wheel
    const options = Array.from(column.querySelectorAll(".date-picker-aq-mobile__option"))
    const selectedIndex = options.findIndex((option) => option.classList.contains("is-selected"))
    const nextIndex = Math.max(0, Math.min(options.length - 1, selectedIndex + (event.key === "ArrowDown" ? 1 : -1)))
    const value = Number(options[nextIndex].dataset.value)

    this.selectMobileWheelValue(wheel, value, true)
  }

  selectMobileWheelValue(wheel, value, shouldScroll) {
    const year = this.mobileDate.getFullYear()
    const month = this.mobileDate.getMonth()
    const day = this.mobileDate.getDate()

    if (wheel === "month") {
      this.mobileDate = this.clampedMobileDate(year, value, day)
    } else if (wheel === "day") {
      this.mobileDate = new Date(year, month, value)
    } else if (wheel === "year") {
      this.mobileDate = this.clampedMobileDate(value, month, day)
    }

    this.renderMobileWheels()
    if (shouldScroll) this.scrollMobileWheelsToDate()
  }

  clampedMobileDate(year, month, day) {
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    return new Date(year, month, Math.min(day, daysInMonth))
  }

  commitTypedValue({ clearInvalid = false } = {}) {
    if (this.inputTarget.disabled || this.inputTarget.readOnly) return

    const value = this.inputTarget.value.trim()

    if (!value) {
      this.clearInputValidity()
      if (this.picker.selectedDates.length === 0) return

      this.picker.clear()
      return
    }

    if (this.modeValue === "range") {
      this.commitTypedRangeValue(value)
      return
    }

    const date = this.parseTypedDate(value)

    if (!date || !this.isAllowedDate(date)) {
      if (clearInvalid) {
        this.clearTypedValue()
        return
      }

      this.setInputInvalid()
      return
    }

    this.clearInputValidity()
    this.picker.setDate(date, true)
  }

  clearTypedValue() {
    this.clearInputValidity()
    this.picker.clear()
    this.inputTarget.value = ""
  }

  commitTypedRangeValue(value) {
    const parts = value.split(/\s+to\s+/i)

    if (parts.length !== 2) {
      this.setInputInvalid()
      return
    }

    const startDate = this.parseTypedDate(parts[0].trim())
    const endDate = this.parseTypedDate(parts[1].trim())

    if (!startDate || !endDate || !this.isAllowedDate(startDate) || !this.isAllowedDate(endDate) || this.dateOnlyTime(startDate) > this.dateOnlyTime(endDate)) {
      this.setInputInvalid()
      return
    }

    this.clearInputValidity()
    this.picker.setDate([startDate, endDate], true)
  }

  parseTypedDate(value) {
    if (value instanceof Date) return new Date(value.getFullYear(), value.getMonth(), value.getDate())
    if (typeof value !== "string") return null

    const normalized = value.replace(/[.\-\s]+/g, "/")
    const separated = normalized.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)

    if (separated) {
      return this.dateFromParts(separated[1], separated[2], separated[3])
    }

    const compact = value.replace(/\D/g, "")
    if (compact.length === 8) return this.dateFromParts(compact.slice(0, 2), compact.slice(2, 4), compact.slice(4))

    return null
  }

  formatDigits(value) {
    const digits = value.replace(/\D/g, "").slice(0, 8)
    const month = digits.slice(0, 2)
    const day = digits.slice(2, 4)
    const year = digits.slice(4, 8)

    return [month, day, year].filter(Boolean).join(this.dateSeparator)
  }

  get dateSeparator() {
    return this.dateFormatValue.includes("-") ? "-" : "/"
  }

  dateFromParts(monthValue, dayValue, yearValue) {
    const month = Number(monthValue)
    const day = Number(dayValue)
    const year = Number(yearValue)

    if (!Number.isInteger(month) || !Number.isInteger(day) || !Number.isInteger(year)) return null
    if (month < 1 || month > 12) return null
    if (day < 1 || day > 31) return null
    if (year < MIN_YEAR || year > MAX_YEAR) return null

    const date = new Date(year, month - 1, day)

    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null

    return date
  }

  isAllowedDate(date) {
    if (date.getFullYear() < MIN_YEAR || date.getFullYear() > MAX_YEAR) return false

    const dateTime = this.dateOnlyTime(date)
    const minDate = this.picker.config.minDate
    const maxDate = this.picker.config.maxDate

    if (minDate && dateTime < this.dateOnlyTime(minDate)) return false
    if (maxDate && dateTime > this.dateOnlyTime(maxDate)) return false

    return true
  }

  dateOnlyTime(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
  }

  addDays(date, days) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days)
  }

  setInputInvalid() {
    this.inputTarget.setCustomValidity(this.invalidMessageValue)
    this.inputTarget.setAttribute("aria-invalid", "true")
    this.element.classList.add("date-input-aq--error")

    const errorId = `${this.inputTarget.id}_error`
    let errorEl = document.getElementById(errorId)
    if (!errorEl) {
      errorEl = document.createElement("p")
      errorEl.id = errorId
      errorEl.className = "date-input-aq__message date-input-aq__message--error"
      errorEl.dataset.clientCreated = "true"
      this.element.appendChild(errorEl)
    }
    errorEl.textContent = this.invalidMessageValue
    this.inputTarget.setAttribute("aria-errormessage", errorId)

    const describedBy = this.inputTarget.getAttribute("aria-describedby") || ""
    if (!describedBy.split(/\s+/).includes(errorId)) {
      this.inputTarget.setAttribute("aria-describedby", describedBy ? `${describedBy} ${errorId}` : errorId)
    }

    this.inputTarget.reportValidity()
  }

  clearInputValidity() {
    this.inputTarget.setCustomValidity("")
    if (this.inputTarget.dataset.serverInvalid !== "true") {
      this.inputTarget.removeAttribute("aria-invalid")
      this.element.classList.remove("date-input-aq--error")
    }

    const errorId = `${this.inputTarget.id}_error`
    const errorEl = document.getElementById(errorId)
    if (errorEl?.dataset.clientCreated === "true") {
      errorEl.remove()
    }
    this.inputTarget.removeAttribute("aria-errormessage")

    const describedBy = this.inputTarget.getAttribute("aria-describedby")
    if (describedBy) {
      const ids = describedBy.split(/\s+/).filter((id) => id !== errorId)
      if (ids.length > 0) {
        this.inputTarget.setAttribute("aria-describedby", ids.join(" "))
      } else {
        this.inputTarget.removeAttribute("aria-describedby")
      }
    }
  }

  submitValue(value) {
    if (!this.hasSubmitUrlValue) return

    window.fetch(this.submitUrlValue, {
      method: this.submitMethodValue.toUpperCase(),
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": this.csrfToken,
      },
      body: JSON.stringify({ [this.inputTarget.name]: value }),
    })
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }

  dispatchChange() {
    const nextValue = this.inputTarget.value
    if (nextValue === this.lastDispatchedValue) return

    this.lastDispatchedValue = nextValue
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  arrowSvg(direction) {
    const path = direction === "next"
      ? "M5 3.5 8.5 8 5 12.5"
      : "M9 3.5 5.5 8 9 12.5"

    return `<svg viewBox="0 0 14 16" aria-hidden="true" focusable="false"><path d="${path}" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`
  }
}
